/**
 * @fileoverview Tracking V2 Client - Session tracking and attribution management
 * with localStorage persistence, SBJS integration, and cross-tab synchronization.
 * @version 2.0.0
 */

import { v4 as uuidv4 } from 'uuid';

const STORAGE_KEY = 'pipefy_tracking_v1';
const STORAGE_VERSION = 1;
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutos
const MAX_VISITS = 50;

const DEFAULT_CONSENT = {
  marketing: true,
  timestamp: Date.now(),
};

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
];

const isBrowser = typeof window !== 'undefined';
const trackingConfig = (isBrowser && window.pipefyTrackingConfig) || {};
const PAGE_CONTEXT_TOKEN =
  trackingConfig.pageContextToken && typeof trackingConfig.pageContextToken === 'object'
    ? trackingConfig.pageContextToken
    : null;

// Whitelist of allowed custom parameter keys (from TrackingManager options)
const ALLOWED_CUSTOM_KEYS = Array.isArray(trackingConfig.allowedCustomKeys)
  ? new Set(trackingConfig.allowedCustomKeys)
  : new Set();

// Standard UTM keys that are always allowed
const STANDARD_UTM_KEYS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
  'campaign_id', // campaign_id is always allowed
]);

/**
 * Safely access localStorage with fallback to null if unavailable.
 * @returns {Storage|null} localStorage or null if unavailable/restricted.
 */
function safeLocalStorage() {
  if (!isBrowser) return null;
  try {
    const testKey = '__pfy_ls_test__';
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    return window.localStorage;
  } catch (error) {
    return null;
  }
}

/**
 * Returns current timestamp in milliseconds.
 * @returns {number} Current timestamp.
 */
function now() {
  return Date.now();
}

/**
 * Get the current document title.
 * @returns {string} Document title or empty string.
 */
function getDocumentTitle() {
  if (!isBrowser) return '';
  return document.title || '';
}

/**
 * Get the current full URL (origin + pathname + search).
 * @returns {string} Full current URL.
 */
function getCurrentUrl() {
  if (!isBrowser) return '';
  return `${window.location.origin}${window.location.pathname}${window.location.search}`;
}

/**
 * Get the current pathname.
 * @returns {string} Current pathname.
 */
function getCurrentPath() {
  if (!isBrowser) return '';
  return window.location.pathname || '';
}

/**
 * Best-effort post/page ID extraction from DOM classes/data attrs.
 * @returns {number|null} Positive post ID or null when unavailable.
 */
function getCurrentPostId() {
  if (!isBrowser) return null;

  const body = document.body;
  if (!body) return null;

  const classes = body.className || '';
  const classMatches = [/\bpostid-(\d+)\b/i, /\bpage-id-(\d+)\b/i];

  for (const regex of classMatches) {
    const match = classes.match(regex);
    if (!match || !match[1]) continue;

    const parsed = Number.parseInt(match[1], 10);
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }
  }

  const dataPostId =
    body.getAttribute('data-post-id') || document.documentElement?.getAttribute('data-post-id');

  if (!dataPostId) {
    return null;
  }

  const parsed = Number.parseInt(dataPostId, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Safely parse JSON string, returning null on failure.
 * @param {string} value - JSON string to parse.
 * @returns {*|null} Parsed value or null.
 */
function parseJSON(value) {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

/**
 * Cookie name for campaign_id persistence
 */
const CAMPAIGN_ID_COOKIE = 'pfy_campaign_id';

/**
 * Cookie expiration in days (similar to SBJS - 1 year)
 */
const CAMPAIGN_ID_COOKIE_EXPIRY_DAYS = 365;

/**
 * Set a cookie with the given name, value, and expiration.
 * @param {string} name - Cookie name.
 * @param {string} value - Cookie value.
 * @param {number} days - Expiration in days.
 */
function setCookie(name, value, days) {
  if (!isBrowser) return;

  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  const expiresStr = expires.toUTCString();

  // Set cookie with SameSite=Lax for better compatibility
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expiresStr};path=/;SameSite=Lax`;
}

/**
 * Get a cookie value by name.
 * @param {string} name - Cookie name.
 * @returns {string|null} Cookie value or null if not found.
 */
function getCookie(name) {
  if (!isBrowser) return null;

  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }

  return null;
}

/**
 * TrackingClient - Main tracking class managing session, visits, UTMs, and attribution.
 * @class
 */
class TrackingClient {
  /**
   * Create a TrackingClient instance.
   * @param {Object} options - Configuration options.
   * @param {string} [options.storageKey] - localStorage key for persistence.
   * @param {number} [options.sessionTimeout] - Session timeout in milliseconds.
   * @param {boolean} [options.debug] - Enable debug mode.
   * @param {boolean|Object} [options.consent] - Initial consent (boolean or object with marketing flag).
   */
  constructor(options = {}) {
    this.storage = safeLocalStorage();
    this.storageKey = options.storageKey || STORAGE_KEY;
    this.sessionTimeout = options.sessionTimeout || SESSION_TIMEOUT_MS;
    this.debug = options.debug ?? Boolean(window.PIPEFY_TRACKING_DEBUG);
    this._writeTimeout = null;
    this._listeners = new Set();
    this.sbjsInitialized = false;

    const consent = this.resolveInitialConsent(options.consent);
    this.state = this.loadState(consent);

    this.bootstrap();
  }

  resolveInitialConsent(explicitConsent) {
    if (typeof explicitConsent === 'boolean') {
      return { marketing: explicitConsent, timestamp: now() };
    }

    const initial = { ...DEFAULT_CONSENT };

    if (typeof window !== 'undefined' && window.PIPEFY_CONSENT) {
      // Compatibilidade com possíveis managers existentes
      const { marketing } = window.PIPEFY_CONSENT;
      if (typeof marketing === 'boolean') {
        initial.marketing = marketing;
      }
    }

    return initial;
  }

  loadState(initialConsent) {
    const fallback = this.createInitialState(initialConsent);

    if (!this.storage) {
      this.log('warn', 'localStorage indisponível, utilizando estado em memória');
      return fallback;
    }

    const raw = this.storage.getItem(this.storageKey);
    if (!raw) {
      return fallback;
    }

    const parsed = parseJSON(raw);
    if (!parsed || parsed.version !== STORAGE_VERSION) {
      this.log('warn', 'estado inválido ou versão incompatível, reiniciando');
      return fallback;
    }

    if (this.isExpired(parsed)) {
      this.log('info', 'sessão expirada, criando nova');
      return this.createInitialState(initialConsent);
    }

    // Merge com defaults para garantir campos obrigatórios
    // Preservar previousPage do parsed se existir, caso contrário usar null do fallback
    const merged = {
      ...fallback,
      ...parsed,
      previousPage:
        parsed.previousPage !== undefined && parsed.previousPage !== null
          ? parsed.previousPage
          : fallback.previousPage,
      previousPageVisit:
        parsed.previousPageVisit && typeof parsed.previousPageVisit === 'object'
          ? parsed.previousPageVisit
          : fallback.previousPageVisit,
      consent: parsed.consent || initialConsent,
    };
    return merged;
  }

  createInitialState(consent) {
    const context = this.buildPageContext();
    const utm = this.extractUtmFromLocation();
    const campaignId = this.extractCampaignIdFromLocation();
    const timestamp = now();

    return {
      version: STORAGE_VERSION,
      sessionId: uuidv4(),
      createdAt: timestamp,
      updatedAt: timestamp,
      lastActivityAt: timestamp,
      pageViews: 0,
      firstVisit: context,
      currentVisit: context,
      previousPage: null,
      previousPageVisit: null,
      visits: [context],
      utm,
      // Preserva os primeiros UTMs capturados na sessão
      firstUtm: Object.keys(utm || {}).length > 0 ? { ...utm } : {},
      referer: context.referer,
      custom: campaignId ? { campaign_id: campaignId } : { campaign_id: '' },
      attribution: {},
      consent,
      flags: {
        debug: this.debug,
      },
    };
  }

  bootstrap() {
    if (!isBrowser) return;

    const init = () => {
      this.initSourcebuster();
      this.updateFromPage({ reason: 'bootstrap' });
      this.patchHistory();
      this.bindEvents();
      this.broadcastChange();
      this.log('info', 'TrackingClient inicializado', this.state);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  }

  bindEvents() {
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.updateLastActivity('visibilitychange');
      }
    });

    window.addEventListener('beforeunload', () => {
      this.updateLastActivity('beforeunload');
      this.flush();
    });

    window.addEventListener('pagehide', () => {
      this.updateLastActivity('pagehide');
      this.flush();
    });

    window.addEventListener('popstate', () => {
      this.updateFromPage({ reason: 'popstate' });
    });

    window.addEventListener('pushstate', () => {
      this.updateFromPage({ reason: 'pushstate' });
    });

    window.addEventListener('replaceState', () => {
      this.updateFromPage({ reason: 'replaceState' });
    });

    window.addEventListener('storage', event => {
      if (event.key !== this.storageKey || !event.newValue) return;
      const incoming = parseJSON(event.newValue);
      if (!incoming) return;
      if (incoming.updatedAt <= this.state.updatedAt) return;
      this.log('info', 'recebendo atualização de outra aba');
      this.state = { ...this.state, ...incoming };
      this.dispatch();
    });
  }

  patchHistory() {
    if (!isBrowser || this._historyPatched) return;
    const { history } = window;
    if (!history) return;

    const dispatch = type => {
      try {
        window.dispatchEvent(new Event(type));
      } catch (error) {
        this.log('debug', 'erro ao despachar evento de histórico', error);
      }
    };

    const originalPushState =
      typeof history.pushState === 'function' ? history.pushState.bind(history) : null;
    if (originalPushState) {
      history.pushState = (...args) => {
        const result = originalPushState(...args);
        dispatch('pushstate');
        return result;
      };
    }

    const originalReplaceState =
      typeof history.replaceState === 'function' ? history.replaceState.bind(history) : null;
    if (originalReplaceState) {
      history.replaceState = (...args) => {
        const result = originalReplaceState(...args);
        dispatch('replaceState');
        return result;
      };
    }

    this._historyPatched = true;
  }

  hasMarketingConsent() {
    return Boolean(this.state?.consent?.marketing);
  }

  initSourcebuster() {
    if (!isBrowser || this.sbjsInitialized || !this.hasMarketingConsent()) {
      return;
    }

    if (typeof sbjs === 'undefined') {
      this.log('info', 'Sourcebuster indisponível; tracking segue sem atribuição sbjs');
      return;
    }

    try {
      sbjs.init();
      this.sbjsInitialized = true;
      this.updateAttributionFromSbjs();
    } catch (error) {
      this.log('warn', 'falha ao inicializar Sourcebuster', error);
    }
  }

  updateAttributionFromSbjs() {
    if (!isBrowser || !this.sbjsInitialized || typeof sbjs === 'undefined') {
      return;
    }

    try {
      const data = sbjs.get;
      if (!data) return;

      const { first, current, first_add: firstAdd, current_add: currentAdd } = data;

      const attribution = this.state.attribution || {};
      const normalizedFirst = this.normalizeSbjsTouch(first);
      if (normalizedFirst && !attribution.first) {
        attribution.first = normalizedFirst;
      }

      const normalizedCurrent = this.normalizeSbjsTouch(current);
      if (normalizedCurrent) {
        attribution.current = normalizedCurrent;
        if (!this.state.referer && normalizedCurrent.referer) {
          this.state.referer = normalizedCurrent.referer;
        }
        // Preservar referer no firstVisit se ainda não tiver
        if (
          normalizedCurrent.referer &&
          (!this.state.firstVisit?.referer || !this.state.firstVisit.referer)
        ) {
          if (!this.state.firstVisit) {
            this.state.firstVisit = {};
          }
          this.state.firstVisit.referer = normalizedCurrent.referer;
        }
      }

      this.state.attribution = attribution;

      const utmFromSbjs = this.extractUtmFromSbjs(current);
      // Ignorar "direct" do SBJS - não é um UTM real
      const hasRealUtm = utmFromSbjs.source && utmFromSbjs.source !== 'direct';
      if (hasRealUtm && Object.keys(this.state.utm || {}).length === 0) {
        // Se ainda não temos UTMs, usar os do Sourcebuster como current e também como first se ainda vazio
        this.state.utm = {
          ...utmFromSbjs,
        };
        if (!this.state.firstUtm || Object.keys(this.state.firstUtm).length === 0) {
          this.state.firstUtm = { ...utmFromSbjs };
        }
      }

      // Check for campaign_id in cookie if not already set (returning visitor scenario)
      if (!this.state.custom?.campaign_id) {
        const campaignIdFromCookie = getCookie(CAMPAIGN_ID_COOKIE);
        if (campaignIdFromCookie) {
          this.state.custom = { ...(this.state.custom || {}), campaign_id: campaignIdFromCookie };
        }
      }

      if (!this.state.firstVisit?.url && firstAdd?.ep) {
        this.state.firstVisit = {
          ...(this.state.firstVisit || {}),
          url: firstAdd.ep,
        };
      }

      if (!this.state.currentVisit?.entryPage && currentAdd?.ep) {
        this.state.currentVisit = {
          ...(this.state.currentVisit || {}),
          entryPage: currentAdd.ep,
        };
      }

      this.persist();
    } catch (error) {
      this.log('debug', 'falha ao atualizar dados do Sourcebuster', error);
    }
  }

  normalizeSbjsTouch(touch) {
    if (!touch || typeof touch !== 'object') {
      return null;
    }

    return {
      type: touch.typ || '',
      source: touch.src || '',
      medium: touch.mdm || touch.mdu || '',
      campaign: touch.cmp || '',
      content: touch.cnt || '',
      term: touch.trm || '',
      channel: touch.cls || touch.chn || '',
      referer: touch.ref || touch.rfr || '',
      entryPage: touch.ep || '',
      timestamp: touch.tms || now(),
    };
  }

  extractUtmFromSbjs(touch) {
    if (!touch || typeof touch !== 'object') {
      return {};
    }

    const utm = {};

    // Normaliza para o mesmo formato de extractUtmFromLocation()
    if (touch.src) utm.source = touch.src;
    if (touch.mdm || touch.mdu) utm.medium = touch.mdm || touch.mdu;
    if (touch.cmp) utm.campaign = touch.cmp;
    if (touch.trm) utm.term = touch.trm;
    if (touch.cnt) utm.content = touch.cnt;

    if (Object.keys(utm).length > 0) {
      utm.capturedAt = now();
      utm.captureSource = 'sbjs';
    }

    return utm;
  }

  buildPageContext() {
    const timestamp = now();
    const title = getDocumentTitle();
    // Forçar recaptura da URL atual usando window.location diretamente
    // Isso garante que temos a URL mais atual, especialmente após navegação
    const url = isBrowser
      ? `${window.location.origin}${window.location.pathname}${window.location.search}`
      : getCurrentUrl();
    const path = getCurrentPath();
    const referer = isBrowser ? document.referrer || '' : '';
    const postId = getCurrentPostId();

    return {
      url,
      path,
      title,
      referer,
      timestamp,
      postId,
      pageContextToken: PAGE_CONTEXT_TOKEN,
    };
  }

  extractUtmFromLocation() {
    if (!isBrowser) return {};
    const params = new URLSearchParams(window.location.search || '');
    const utm = {};
    let hasUtm = false;

    UTM_KEYS.forEach(key => {
      const value = params.get(key);
      if (value) {
        // Normalizar chave (utm_source -> source, gclid -> gclid, etc)
        const normalizedKey = key.startsWith('utm_') ? key.replace('utm_', '') : key;
        utm[normalizedKey] = value;
        hasUtm = true;
      }
    });

    if (hasUtm) {
      utm.capturedAt = now();
      utm.captureSource = 'url'; // Origem da captura, não sobrescreve utm_source
    }

    return utm;
  }

  extractCampaignIdFromLocation() {
    if (!isBrowser) return '';
    const params = new URLSearchParams(window.location.search || '');
    const campaignIdFromUrl = params.get('campaign_id') || '';

    // If campaign_id is in URL, use it and save to cookie
    if (campaignIdFromUrl) {
      setCookie(CAMPAIGN_ID_COOKIE, campaignIdFromUrl, CAMPAIGN_ID_COOKIE_EXPIRY_DAYS);
      return campaignIdFromUrl;
    }

    // If not in URL, try to get from cookie (returning visitor)
    const campaignIdFromCookie = getCookie(CAMPAIGN_ID_COOKIE);
    if (campaignIdFromCookie) {
      return campaignIdFromCookie;
    }

    return '';
  }

  /**
   * Extract allowed custom parameters from URL based on whitelist.
   * Only extracts parameters that are:
   * 1. Standard UTM keys (always allowed)
   * 2. campaign_id (always allowed)
   * 3. Keys defined in TrackingManager options (allowedCustomKeys)
   *
   * @returns {Object} Object with allowed custom parameters.
   */
  extractAllowedCustomParameters() {
    if (!isBrowser) return {};

    const params = new URLSearchParams(window.location.search || '');
    const custom = {};
    const skipped = [];

    // Iterate through all URL parameters
    for (const [key, value] of params.entries()) {
      // Skip if already handled by UTM extraction
      if (UTM_KEYS.includes(key)) {
        continue;
      }

      // Skip campaign_id (handled separately)
      if (key === 'campaign_id') {
        continue;
      }

      // Only include if key is in whitelist (from TrackingManager)
      if (ALLOWED_CUSTOM_KEYS.has(key)) {
        custom[key] = value;
        if (this.debug) {
          this.log('debug', 'extractAllowedCustomParameters: allowed key', { key, value });
        }
      } else {
        skipped.push(key);
        if (this.debug) {
          this.log('debug', 'extractAllowedCustomParameters: skipped key (not in whitelist)', {
            key,
            value,
            whitelistSize: ALLOWED_CUSTOM_KEYS.size,
          });
        }
      }
    }

    if (this.debug && skipped.length > 0) {
      this.log('info', 'extractAllowedCustomParameters: skipped keys', {
        skipped,
        allowed: Object.keys(custom),
        whitelist: Array.from(ALLOWED_CUSTOM_KEYS),
      });
    }

    return custom;
  }

  updateFromPage({ reason }) {
    const context = this.buildPageContext();
    const utm = this.extractUtmFromLocation();
    const campaignId = this.extractCampaignIdFromLocation();
    const allowedCustomParams = this.extractAllowedCustomParameters();

    const shouldOverwriteUtm = Object.keys(utm).length > 0;
    if (shouldOverwriteUtm) {
      this.state.utm = utm;
      this.state.referer = context.referer;
      // Se ainda não tínhamos firstUtm, preserva o primeiro conjunto capturado
      if (!this.state.firstUtm || Object.keys(this.state.firstUtm).length === 0) {
        this.state.firstUtm = { ...utm };
      }
    }

    // Merge allowed custom parameters (only whitelisted keys from TrackingManager)
    if (Object.keys(allowedCustomParams).length > 0) {
      this.state.custom = { ...(this.state.custom || {}), ...allowedCustomParams };
    }

    if (campaignId) {
      this.state.custom = { ...(this.state.custom || {}), campaign_id: campaignId };
      // Persist campaign_id to cookie for returning visitors
      setCookie(CAMPAIGN_ID_COOKIE, campaignId, CAMPAIGN_ID_COOKIE_EXPIRY_DAYS);
    }

    if (!this.state.firstVisit || !this.state.firstVisit.timestamp) {
      this.state.firstVisit = context;
      // Preservar referer do first visit se disponível
      if (context.referer && !this.state.firstVisit.referer) {
        this.state.firstVisit.referer = context.referer;
      }
    } else if (context.referer && !this.state.firstVisit.referer) {
      // Se o first visit já existe mas não tem referer, e temos um referer agora, preservar
      this.state.firstVisit.referer = context.referer;
    }

    // IMPORTANTE: Salvar previousPage ANTES de verificar isSameUrl e atualizar currentVisit
    // Se já temos uma currentVisit com URL diferente da atual, ela vira a previousPage
    // Isso funciona tanto para navegação normal quanto para bootstrap após navegação
    const hasCurrentVisit = !!this.state.currentVisit?.url;

    // No bootstrap, usar window.location diretamente para garantir URL atual
    // Para outras razões, usar context.url
    const currentUrlForComparison =
      isBrowser && reason === 'bootstrap'
        ? `${window.location.origin}${window.location.pathname}${window.location.search}`
        : context.url;
    const urlsDiffer = hasCurrentVisit && this.state.currentVisit.url !== currentUrlForComparison;

    // No bootstrap após navegação, atualizar previousPage se temos currentVisit do localStorage
    // e a URL do currentVisit é diferente da URL atual (indicando navegação)
    // Isso garante que capturamos a página anterior mesmo se window.location ainda não foi atualizado
    // IMPORTANTE: Só atualizar se as URLs forem diferentes para não atualizar na primeira visita
    if (reason === 'bootstrap' && hasCurrentVisit) {
      // Usar window.location diretamente para garantir que temos a URL mais atual
      const actualCurrentUrl = isBrowser
        ? `${window.location.origin}${window.location.pathname}${window.location.search}`
        : context.url;

      // Só atualizar previousPage se a URL do currentVisit for diferente da URL atual
      // Isso evita atualizar na primeira visita quando currentVisit tem a mesma URL
      if (this.state.currentVisit.url !== actualCurrentUrl) {
        this.state.previousPage = this.state.currentVisit.url;
        this.state.previousPageVisit = { ...this.state.currentVisit };
      }
    } else if (urlsDiffer) {
      // Para outras razões (navegação normal), usar a lógica original
      this.state.previousPage = this.state.currentVisit.url;
      this.state.previousPageVisit = { ...this.state.currentVisit };
    }

    const isSameUrl = this.state.currentVisit?.url === context.url;
    const allowIncrement = reason === 'bootstrap' || !isSameUrl;

    if (allowIncrement) {
      // Garantir que currentVisit tenha a URL atual, mesmo se context.url ainda não foi atualizado
      // Isso é especialmente importante após page.goto() quando window.location pode não estar atualizado
      if (isBrowser && reason === 'bootstrap') {
        const actualUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
        if (actualUrl !== context.url) {
          context.url = actualUrl;
          context.path = window.location.pathname;
        }
      }

      this.state.currentVisit = context;
      this.pushVisit(context);
      this.state.pageViews += 1;
    } else {
      this.state.currentVisit = {
        ...this.state.currentVisit,
        title: context.title || this.state.currentVisit.title,
        timestamp: context.timestamp,
        referer: context.referer || this.state.currentVisit.referer,
        pageContextToken:
          context.pageContextToken || this.state.currentVisit.pageContextToken || null,
      };
    }

    if (this.hasMarketingConsent()) {
      if (!this.sbjsInitialized) {
        this.initSourcebuster();
      } else {
        this.updateAttributionFromSbjs();
      }
    }

    this.updateLastActivity(reason || 'updateFromPage');
    this.persist();
    this.dispatch();
  }

  pushVisit(visit) {
    const visits = Array.isArray(this.state.visits) ? [...this.state.visits] : [];
    const last = visits[visits.length - 1];
    if (last && last.url === visit.url) {
      visits[visits.length - 1] = { ...last, ...visit };
    } else {
      visits.push(visit);
    }
    if (visits.length > MAX_VISITS) {
      visits.splice(0, visits.length - MAX_VISITS);
    }
    this.state.visits = visits;
  }

  updateLastActivity(reason) {
    this.state.lastActivityAt = now();
    this.state.updatedAt = this.state.lastActivityAt;
    this.state.flags = {
      ...this.state.flags,
      lastReason: reason,
    };
  }

  isExpired(state) {
    if (!state) return true;
    const lastActivity = state.lastActivityAt || state.updatedAt || state.createdAt;
    return now() - lastActivity > this.sessionTimeout;
  }

  persist() {
    if (!this.storage) return;

    if (this._writeTimeout) {
      clearTimeout(this._writeTimeout);
    }
    this._writeTimeout = setTimeout(() => this.flush(), 150);
  }

  flush() {
    if (!this.storage) return;
    if (this._writeTimeout) {
      clearTimeout(this._writeTimeout);
      this._writeTimeout = null;
    }
    const payload = JSON.stringify(this.state);
    try {
      this.storage.setItem(this.storageKey, payload);
    } catch (error) {
      this.log('error', 'falha ao gravar no localStorage', error);
    }
  }

  broadcastChange() {
    this.persist();
    this.dispatch();
  }

  dispatch() {
    this._listeners.forEach(listener => {
      try {
        listener(this.state);
      } catch (error) {
        this.log('error', 'listener error', error);
      }
    });
  }

  on(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  setConsent(typeOrConsent, value) {
    let marketing;

    // Aceitar tanto setConsent('marketing', false) quanto setConsent({marketing: false})
    if (typeof typeOrConsent === 'string' && typeOrConsent === 'marketing') {
      marketing = Boolean(value);
    } else if (typeof typeOrConsent === 'object' && typeOrConsent !== null) {
      marketing =
        typeof typeOrConsent.marketing === 'boolean'
          ? typeOrConsent.marketing
          : this.state.consent.marketing;
    } else {
      return; // Argumento inválido
    }

    this.state.consent = {
      marketing,
      timestamp: now(),
    };

    if (marketing) {
      this.initSourcebuster();
    } else {
      this.sbjsInitialized = false;
    }

    this.broadcastChange();
  }

  /**
   * Set or merge custom parameters into the tracking state.
   * Only parameters in the whitelist (from TrackingManager) are allowed.
   * campaign_id is always allowed.
   *
   * @param {Object} custom - Custom parameters to merge.
   */
  setCustomParameters(custom = {}) {
    if (typeof custom !== 'object' || custom === null) return;

    // Filter to only include whitelisted keys
    const filtered = {};
    const skipped = [];

    for (const [key, value] of Object.entries(custom)) {
      // campaign_id is always allowed
      if (key === 'campaign_id') {
        filtered[key] = value;
        continue;
      }

      // Only include if key is in whitelist (from TrackingManager)
      if (ALLOWED_CUSTOM_KEYS.has(key)) {
        filtered[key] = value;
        if (this.debug) {
          this.log('debug', 'setCustomParameters: allowed key', { key, value });
        }
      } else {
        skipped.push(key);
        if (this.debug) {
          this.log('warn', 'setCustomParameters: skipped key (not in whitelist)', {
            key,
            value,
            whitelistSize: ALLOWED_CUSTOM_KEYS.size,
          });
        }
      }
    }

    if (this.debug && skipped.length > 0) {
      this.log('info', 'setCustomParameters: filtered custom parameters', {
        skipped,
        allowed: Object.keys(filtered),
        whitelist: Array.from(ALLOWED_CUSTOM_KEYS),
      });
    }

    this.state.custom = { ...this.state.custom, ...filtered };
    this.broadcastChange();
  }

  /**
   * Get a normalized snapshot of the current tracking state.
   * Includes session, visits (first/current/history), UTMs, consent, attribution, and metadata.
   * @returns {Object} Normalized snapshot object.
   */
  getSnapshot() {
    const duration = Math.max(
      0,
      (this.state.lastActivityAt || now()) - (this.state.createdAt || now())
    );

    return {
      version: STORAGE_VERSION,
      session: {
        id: this.state.sessionId,
        created_at: this.state.createdAt,
        last_activity_at: this.state.lastActivityAt,
        page_views: this.state.pageViews,
      },
      consent: this.state.consent,
      utm: {
        source: this.state.utm?.source || '',
        medium: this.state.utm?.medium || '',
        campaign: this.state.utm?.campaign || '',
        term: this.state.utm?.term || '',
        content: this.state.utm?.content || '',
        gclid: this.state.utm?.gclid || '',
        fbclid: this.state.utm?.fbclid || '',
        msclkid: this.state.utm?.msclkid || '',
        ...(this.state.utm || {}),
      },
      visits: {
        first: { ...(this.state.firstVisit || {}), utm: this.state.firstUtm || {} },
        current: this.state.currentVisit,
        history: this.state.visits || [],
      },
      previousPage: this.state.previousPage || '',
      previousPageVisit: this.state.previousPageVisit || null,
      referer: this.state.referer,
      custom: {
        ...(this.state.custom || {}),
        campaign_id:
          this.state.custom &&
          typeof this.state.custom.campaign_id !== 'undefined' &&
          this.state.custom.campaign_id !== null
            ? String(this.state.custom.campaign_id)
            : '',
      },
      attribution: this.state.attribution || {},
      meta: {
        duration,
        generated_at: now(),
      },
    };
  }

  /**
   * Get flattened lead data ready for CRM payloads (Salesforce, HubSpot, etc.).
   * Includes session fields, UTM parameters, referer (preferring external), campaign_id, consent, and custom parameters.
   * @returns {Object} Flattened lead data object.
   */
  getLeadData() {
    const snapshot = this.getSnapshot();
    const { visits, utm, consent } = snapshot;
    const first = visits.first || {};
    const current = visits.current || {};

    // Preferir referer externo (primeiro que não seja same-origin). Se não houver, vazio.
    let computedReferer = '';
    if (isBrowser) {
      const sameOrigin = url => {
        try {
          const u = new URL(url);
          return u.origin === window.location.origin;
        } catch {
          return false;
        }
      };
      const refs = [first.referer, this.state.referer, current.referer].filter(Boolean);
      const external = refs.find(r => !sameOrigin(r));
      computedReferer = external || '';
    } else {
      computedReferer = current.referer || this.state.referer || '';
    }

    // Determinar previous_page: usar previousPage direto ou fallback para penúltima entrada do histórico
    let previousPage = snapshot.previousPage || '';
    if (!previousPage && Array.isArray(visits.history) && visits.history.length >= 2) {
      // Se não temos previousPage direto, usar penúltima entrada do histórico
      const historyLength = visits.history.length;
      previousPage = visits.history[historyLength - 2]?.url || '';
    }

    return {
      session_id: snapshot.session.id,
      user_first_page: first.url || '',
      current_page: current.url || '',
      previous_page: previousPage,
      previous_page_token: (() => {
        const h = Array.isArray(visits.history) ? visits.history : [];
        const prev = h.length >= 2 ? h[h.length - 2] : null;
        const token = prev?.pageContextToken ?? null;
        if (token === null || typeof token !== 'object') return null;
        return token;
      })(),
      referer: computedReferer,
      // UTMs flatten no nível raiz
      utm_source: utm.source || '',
      utm_medium: utm.medium || '',
      utm_campaign: utm.campaign || '',
      utm_term: utm.term || '',
      utm_content: utm.content || '',
      gclid: utm.gclid || '',
      fbclid: utm.fbclid || '',
      msclkid: utm.msclkid || '',
      // Outros campos
      campaign_id:
        this.state.custom && this.state.custom.campaign_id ? this.state.custom.campaign_id : '',
      previous_page_post_id: (() => {
        const h = Array.isArray(visits.history) ? visits.history : [];
        const prev = h[h.length - 2] ?? null;
        return prev?.pageContextToken?.decoded?.post_id ?? prev?.postId ?? null;
      })(),
      organic_traffic_id:
        snapshot.organic_traffic_id ?? this.state.custom?.organic_traffic_id ?? '',
      has_consent: Boolean(consent?.marketing),
      session_duration: snapshot.meta.duration,
      page_views: snapshot.session.page_views,
      first_visit_timestamp: first.timestamp || this.state.createdAt,
      current_timestamp: now(),
      ...this.state.custom, // Custom parameters flatten também
    };
  }

  /**
   * Clear all tracking data and reset to initial state.
   * Preserves current consent settings.
   */
  clear() {
    if (this.storage) {
      this.storage.removeItem(this.storageKey);
    }
    this.state = this.createInitialState(this.state.consent || DEFAULT_CONSENT);
    this.broadcastChange();
  }

  /**
   * Internal logging utility respecting debug mode.
   * @param {string} level - Log level (info, debug, warn, error).
   * @param {...*} args - Arguments to log.
   */
  log(level, ...args) {
    if (!this.debug && level === 'debug') return;
    const prefix = '[TrackingClient]';
    const fn = console[level] || console.log;
    fn.call(console, prefix, ...args);
  }
}

const initialConsent =
  typeof trackingConfig.initialConsent === 'boolean' ? trackingConfig.initialConsent : undefined;

const clientOptions = {
  storageKey: trackingConfig.storageKey,
  sessionTimeout: trackingConfig.sessionTimeoutMs,
  consent: initialConsent,
  debug: trackingConfig.debug,
};

const trackingClient = isBrowser ? new TrackingClient(clientOptions) : null;

if (isBrowser) {
  window.PipefyTracking = trackingClient;
}

export default trackingClient;
export { TrackingClient };
